// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import type { GeometriEditor, GestureStatus } from '../../app/bygning/GeometriEditorContext';
import {
  dimDirection,
  dimGeometry,
  dimOffset,
  guideLineEnds,
  guideSnaps,
  tapeGuide,
  tapeReading,
  tapeStart,
  type DimGeometry,
  type NewGuide,
  type TapeReading,
  type TapeStart,
} from '../../bygning/cadMeasure';
import { lengthInput, parseLength } from '../../bygning/geometriEdit';
import { INFERENCE_TOKEN, infer, manhattanAngle, type Inference, type SnapSegment } from '../../bygning/inference';
import type { XY } from '../../bygning/planWalls';
import type { ViewportHandle } from '../../viewport/Viewport';
import { Glyph, ScreenTip, TrackLines } from './GeometriLayer';
import { useSmartTracking } from './useSmartTracking';
import geo from './GeometriLayer.module.css';
import styles from './MeasureToolLayer.module.css';

/** The inference engine's reach, px. */
const SNAP_PX = 12;
/** A chain's line stands this far (px) from its points until it is placed. */
const DIM_GAP_PX = 28;
/** Within this far (px) of the bottom the measurement box goes above the pointer (the strip and the dock live there). */
const BOX_CLEAR_PX = 240;
/** Two clicks this close (px) on the last point end the picking (a double click). */
const SAME_PX = 4;

export interface MeasureToolLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  host: RefObject<HTMLElement | null>;
  editor: GeometriEditor;
  storeyId: number | null;
  z: number;
}

interface Pointer {
  raw: XY;
  inf: Inference;
}

/** Målsætning's picking: the points so far, and whether the line is being placed. */
interface DimDraft {
  points: XY[];
  placing: boolean;
  /** The edge the first point was picked on: the chain measures square to it. */
  ref: XY | null;
}

const fmt = (v: number) => lengthInput(v);
const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const signed = (v: number) => `${v < 0 ? '−' : ''}${fmt(Math.abs(v))}`;

/**
 * Rediger › Geometri's measuring on the locked plan (frontend-u0o.11, S9):
 *
 *  - (the guides and dimension chains kept are drawn, picked and edited by
 *    AnnotationLayer, whatever the tool — SketchUp draws guides always);
 *  - Mål (M), the tape measure: click, click; the inference engine snaps
 *    both ends (guides included), the readout runs in the status line and
 *    on the plan. Hooked on an edge it measures square to it. With
 *    "Hjælpelinje" in the strip — or Ctrl, held at the first click or
 *    tapped while the tape is out, flipping the strip's choice — the second
 *    click leaves a guide instead: a line parallel to the edge, or a point;
 *    a typed number places it at that distance;
 *  - Målsætning (⇧M): click the points of a chain, Enter (or a second
 *    click on the last point) ends the picking, the pointer places the
 *    line (or a typed offset) and a click keeps it.
 *
 * Nothing here writes to the model, so a viewer may measure too; guides
 * and chains are kept in this browser (`cadAnnotations.ts`).
 */
export function MeasureToolLayer({ viewport, host, editor, storeyId, z }: MeasureToolLayerProps) {
  const [, setTick] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const [pointer, setPointerState] = useState<Pointer | null>(null);
  const [tape, setTapeState] = useState<TapeStart | null>(null);
  const [last, setLast] = useState<TapeReading | null>(null);
  const [draft, setDraftState] = useState<DimDraft | null>(null);
  const [typed, setTypedState] = useState<string | null>(null);
  /** Ctrl flips "Andet klik" for this measurement: held at the first click, or tapped while the tape is out (SketchUp). */
  const [flip, setFlipState] = useState(false);
  const live = useRef({ editor, storeyId, z, pointer, tape, draft, typed, flip });
  live.current = { editor, storeyId, z, pointer, tape, draft, typed, flip };
  const setFlip = (f: boolean) => {
    live.current.flip = f;
    setFlipState(f);
  };
  const setPointer = (p: Pointer | null) => {
    live.current.pointer = p;
    setPointerState(p);
  };
  const setTape = (t: TapeStart | null) => {
    live.current.tape = t;
    setTapeState(t);
  };
  const setDraft = (d: DimDraft | null) => {
    live.current.draft = d;
    setDraftState(d);
  };
  const setTyped = (t: string | null) => {
    live.current.typed = t;
    setTypedState(t);
  };

  const tool = editor.tool;
  const active = editor.active && (tool === 'measure' || tool === 'dimension');
  // Smart guides' hover-acquired tracking points, per tool.
  const { tracked, points: trackedNow, observe } = useSmartTracking(active ? tool : null);

  // ------------------------------------------------------- projection --
  useEffect(() => {
    let frameId: number | null = null;
    const bump = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(bump);
    if (layerRef.current) observer?.observe(layerRef.current);
    bump();
    return () => {
      off();
      observer?.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [viewport]);

  const toScreen = useCallback(
    (p: XY): [number, number] | null => {
      const s = viewport.current?.projectWorld(new Float64Array([p[0], p[1], live.current.z]), { clip: false });
      return s && Number.isFinite(s[0]) && Number.isFinite(s[1]) ? [s[0], s[1]] : null;
    },
    [viewport],
  );
  const metresPer = useCallback(
    (px: number): number => {
      const a = viewport.current?.planPoint(0, 0, live.current.z);
      const b = viewport.current?.planPoint(px, 0, live.current.z);
      return a && b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0.1;
    },
    [viewport],
  );

  // -------------------------------------------------- inference input --
  const model = editor.model;
  const snap = editor.snapTargets;
  const segments = useMemo<SnapSegment[]>(() => {
    const out: SnapSegment[] = [];
    for (const w of model.walls ?? []) {
      if (storeyId !== null && w.storey_id !== storeyId) continue;
      out.push({ a: w.start, b: w.end, source: 'wall', label: `Væg ${w.id}`, guid: w.guid });
    }
    for (const t of snap?.plane_traces ?? []) out.push({ a: t.start, b: t.end, source: 'trace', label: `P-${t.plane_label}`, points: t.point_count });
    return out;
  }, [model.walls, snap, storeyId]);
  const guides = useMemo(() => guideSnaps(editor.annotations.guides, storeyId), [editor.annotations.guides, storeyId]);
  const axisAngle = useMemo(() => manhattanAngle(snap?.axis_angles, segments), [snap, segments]);
  const inputs = useRef({ segments, guides, axisAngle });
  inputs.current = { segments, guides, axisAngle };

  /** The inference at plan point `raw`, from the tape's hook or the chain's last point. */
  const resolve = useCallback(
    (raw: XY, alt: boolean): Pointer => {
      const { tape: t, draft: d, editor: ed } = live.current;
      const { segments: segs, guides: g, axisAngle: angle } = inputs.current;
      const origin = t?.p ?? (d && !d.placing && d.points.length > 0 ? d.points[d.points.length - 1] : null);
      const inf = infer(raw, {
        segments: [...segs, ...g.segments],
        points: g.points,
        axisAngle: angle,
        origin,
        toggles: ed.snap,
        tolerance: metresPer(SNAP_PX),
        suspended: alt,
        tracked: trackedNow(),
      });
      observe(inf);
      return { raw, inf };
    },
    [metresPer, trackedNow, observe],
  );

  // --------------------------------------------------------- the tools --
  const guideMode = (flipped: boolean) => live.current.editor.measureOptions.guides !== flipped;

  /** The guide a second click (or Enter) leaves with the pointer at `p`. */
  const pendingGuide = (t: TapeStart, p: XY, typedText: string | null): NewGuide | null =>
    tapeGuide(t, p, typedText ? parseLength(typedText) : null, live.current.storeyId);

  /** The chain being drawn, with the pointer as its next point (picking) or as the line's place. */
  const draftChain = (d: DimDraft, p: XY | null, typedText: string | null) => {
    const pts = !d.placing && p ? [...d.points, p] : d.points;
    const dir = dimDirection(pts, live.current.editor.measureOptions.orient, inputs.current.axisAngle, d.ref);
    if (!dir) return null;
    let offset = metresPer(DIM_GAP_PX);
    if (d.placing && p) {
      const raw = dimOffset(pts, dir, p);
      const t = typedText ? parseLength(typedText) : null;
      offset = t !== null ? (raw < 0 ? -t : t) : raw;
    }
    return { points: pts, dir, offset };
  };

  const click = (p: Pointer, withCtrl: boolean) => {
    const { editor: ed, tape: t, draft: d, typed: tt, storeyId: sid, flip: f } = live.current;
    if (ed.tool === 'measure') {
      if (!t) {
        setTape(tapeStart(p.inf, inputs.current.axisAngle));
        setFlip(withCtrl);
        setLast(null);
        return;
      }
      if (guideMode(f)) {
        const g = pendingGuide(t, p.inf.point, tt);
        if (g) ed.annotate({ type: 'add-guide', guide: g });
        else ed.notify('Hjælpelinjen ville ligge oven i udgangspunktet — flyt markøren væk fra det');
      } else setLast(tapeReading(t, p.inf.point));
      setTape(null);
      setFlip(false);
      setTyped(null);
      return;
    }
    // Målsætning.
    if (!d) {
      const seg = p.inf.segment;
      const onEdge = seg && (p.inf.kind === 'on-edge' || p.inf.kind === 'trace' || p.inf.kind === 'guide');
      setDraft({ points: [p.inf.point], placing: false, ref: onEdge ? sub(seg.b, seg.a) : null });
      return;
    }
    if (d.placing) {
      const c = draftChain(d, p.inf.point, tt);
      if (c) ed.annotate({ type: 'add-dim', dim: { storeyId: sid, ...c } });
      setDraft(null);
      setTyped(null);
      return;
    }
    const lastPt = d.points[d.points.length - 1];
    if (dist(p.inf.point, lastPt) <= metresPer(SAME_PX)) {
      if (d.points.length >= 2) setDraft({ ...d, placing: true });
      return;
    }
    setDraft({ ...d, points: [...d.points, p.inf.point] });
  };
  const clickRef = useRef(click);
  clickRef.current = click;

  // ------------------------------------------------- pointer listeners --
  useEffect(() => {
    const el = host.current;
    if (!el || !active) return;
    const planAt = (e: PointerEvent): XY | null => {
      const r = layerRef.current?.getBoundingClientRect();
      if (!r) return null;
      const hit = viewport.current?.planPoint(e.clientX - r.left, e.clientY - r.top, live.current.z);
      return hit ? [hit[0], hit[1]] : null;
    };
    const onMove = (e: PointerEvent) => {
      const raw = planAt(e);
      if (!raw) return;
      setPointer(resolve(raw, e.altKey));
      live.current.editor.cursor.set(raw);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || !(e.target instanceof HTMLCanvasElement)) return;
      e.stopPropagation();
      e.preventDefault();
      const raw = planAt(e);
      if (!raw) return;
      const p = resolve(raw, e.altKey);
      setPointer(p);
      clickRef.current(p, e.ctrlKey);
    };
    // Ctrl-click opens the context menu on a Mac: here it leaves a guide.
    const onMenu = (e: MouseEvent) => {
      if (e.target instanceof HTMLCanvasElement && e.ctrlKey && e.button === 0) e.preventDefault();
    };
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('contextmenu', onMenu, true);
    const prevCursor = el.style.cursor;
    el.style.cursor = 'crosshair';
    return () => {
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('contextmenu', onMenu, true);
      el.style.cursor = prevCursor;
    };
  }, [host, viewport, active, resolve]);

  // Another tool, storey or leaving Geometri starts afresh.
  useEffect(() => {
    setTape(null);
    setDraft(null);
    setTyped(null);
    setPointer(null);
    setLast(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, storeyId, editor.active]);

  // ------------------------------------------------------------- keys --
  // Only mid-gesture: otherwise the editor's keys (tools, ⌫ = the last guide / chain, Esc) hold.
  const gestureOn = active && (tape !== null || draft !== null);
  useEffect(() => {
    if (!gestureOn) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest('input, textarea, select, [role="dialog"]')) return;
      const { tape: t, draft: d, typed: tt, pointer: p, flip: c } = live.current;
      const stop = () => {
        e.preventDefault();
        e.stopPropagation();
      };
      // A typed number places a guide (Mål) or the chain's line (Målsætning).
      const typing = (t !== null && guideMode(c)) || (d !== null && d.placing);
      if (e.key === 'Control') {
        if (t && !e.repeat) {
          setFlip(!c);
          setTyped(null);
        }
        return;
      }
      if (e.key === 'Escape') {
        stop();
        if (tt) setTyped(null);
        else if (d?.placing) setDraft({ ...d, placing: false });
        else {
          setTape(null);
          setFlip(false);
          setDraft(null);
        }
        return;
      }
      if (e.key === 'Enter') {
        stop();
        if (d && !d.placing) {
          if (d.points.length >= 2) setDraft({ ...d, placing: true });
          return;
        }
        if (p) clickRef.current(p, c);
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        stop();
        if (tt) setTyped(tt.slice(0, -1) || null);
        else if (d && !d.placing) setDraft(d.points.length > 1 ? { ...d, points: d.points.slice(0, -1) } : null);
        else if (d?.placing) setDraft({ ...d, placing: false });
        else {
          setTape(null);
          setFlip(false);
        }
        return;
      }
      if (/^[0-9.,]$/.test(e.key) && !e.metaKey && typing) {
        stop();
        setTyped((tt ?? '') + e.key);
        return;
      }
      // Every other letter waits: no tool switch mid-measurement.
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey) stop();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gestureOn]);

  // ------------------------------------------------------- the readout --
  const at = pointer?.inf.point ?? null;
  const reading = tape && at ? tapeReading(tape, at) : null;
  const makingGuide = tape !== null && guideMode(flip);
  const guidePreview = tape && at && makingGuide ? pendingGuide(tape, at, typed) : null;
  const chain = draft ? draftChain(draft, at, typed) : null;
  const chainGeo: DimGeometry | null = chain ? dimGeometry(chain) : null;

  const gesture = useMemo<GestureStatus | null>(() => {
    if (!active) return null;
    if (tape) {
      const keys = [
        { key: 'Ctrl', text: makingGuide ? 'mål i stedet' : 'hjælpelinje' },
        ...(makingGuide ? [{ key: '0–9', text: 'skriv afstand' }] : []),
        { key: 'Alt', text: 'uden snap' },
        { key: 'Esc', text: 'annullér' },
      ];
      const lead = makingGuide
        ? tape.line
          ? `Hjælpelinje · parallel med ${tape.label ?? 'kanten'}`
          : 'Hjælpepunkt · klik hvor det skal ligge'
        : tape.line
          ? `Mål · vinkelret fra ${tape.label ?? 'kanten'}`
          : 'Mål · klik slutpunkt';
      const readout = reading ? `Afstand ${fmt(reading.value)} m · Δx ${signed(reading.dx)} · Δy ${signed(reading.dy)}` : null;
      return { hint: { lead, keys }, readout };
    }
    if (draft) {
      const keys = draft.placing
        ? [
            { key: '0–9', text: 'skriv afstand' },
            { key: 'Esc', text: 'tilbage til punkterne' },
          ]
        : [
            { key: '↵', text: 'placér målkæden' },
            { key: '⌫', text: 'fjern punkt' },
            { key: 'Alt', text: 'uden snap' },
            { key: 'Esc', text: 'annullér' },
          ];
      const n = draft.points.length;
      const lead = draft.placing ? 'Målsætning · klik hvor målkæden skal stå' : `Målsætning · ${n} ${n === 1 ? 'punkt' : 'punkter'} · klik næste`;
      const readout = chainGeo ? `I alt ${fmt(chainGeo.total)} m${chainGeo.stretches.length > 1 ? ` · ${chainGeo.stretches.length} mål` : ''}` : null;
      return { hint: { lead, keys }, readout };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tape, draft, makingGuide, reading?.value, reading?.dx, reading?.dy, chainGeo?.total, chainGeo?.stretches.length]);
  useEffect(() => {
    editor.gesture.set(gesture);
  }, [editor.gesture, gesture]);
  useEffect(() => () => live.current.editor.gesture.set(null), []);

  // ------------------------------------------------------------- draw --
  const poly = (pts: XY[]): string | null => {
    const out: string[] = [];
    for (const p of pts) {
      const s = toScreen(p);
      if (!s) return null;
      out.push(`${s[0].toFixed(1)},${s[1].toFixed(1)}`);
    }
    return out.join(' ');
  };
  const label = (p: XY, text: string, cls: string, key: string) => {
    const s = toScreen(p);
    if (!s) return null;
    return (
      <span key={key} className={cls} style={{ transform: `translate(${s[0].toFixed(1)}px, ${s[1].toFixed(1)}px) translate(-50%, -50%)` }}>
        {text}
      </span>
    );
  };

  /** A chain's lines: the dimension line, the witnesses, a slash at every station. */
  const chainLines = (g: DimGeometry, key: string, cls: string) => {
    const line = poly(g.line);
    return (
      <g key={key} className={cls}>
        {line && <polyline points={line} />}
        {g.witness.map(([a, b], i) => {
          const d = poly([a, b]);
          return d ? <polyline key={`w${i}`} points={d} className={styles.witness} /> : null;
        })}
        {g.ticks.map((t, i) => {
          const s = toScreen(t);
          return s ? <path key={`t${i}`} d={`M${s[0] - 4} ${s[1] + 4}L${s[0] + 4} ${s[1] - 4}`} className={styles.tick} /> : null;
        })}
      </g>
    );
  };

  const inf = active && pointer && !typed ? pointer.inf : null;
  const infAt = inf && inf.kind !== 'free' ? toScreen(inf.point) : null;
  const axisGuide = inf?.guide ? poly([inf.guide[0], inf.guide[1]]) : null;
  const pointerAt = at ? toScreen(at) : null;
  const shownReading = reading ?? (active ? last : null);
  const boxField = makingGuide ? 'Afstand' : draft?.placing ? 'Afstand' : null;
  const boxLive = makingGuide && reading ? fmt(reading.value) : draft?.placing && chain ? fmt(Math.abs(chain.offset)) : '';

  return (
    <div ref={layerRef} className={geo.layer}>
      <svg className={geo.svg} aria-hidden="true">
        {/* What the click will make (the kept guides and chains are AnnotationLayer's). */}
        {guidePreview?.kind === 'line' &&
          (() => {
            const d = poly(guideLineEnds(guidePreview));
            return d ? <polyline points={d} className={`${styles.guideLine} ${styles.preview}`} /> : null;
          })()}
        {guidePreview?.kind === 'point' &&
          (() => {
            const s = toScreen(guidePreview.p);
            return s ? <path d={`M${s[0] - 6} ${s[1]}H${s[0] + 6}M${s[0]} ${s[1] - 6}V${s[1] + 6}`} className={`${styles.guidePoint} ${styles.preview}`} /> : null;
          })()}
        {chainGeo && chainLines(chainGeo, 'draft', `${styles.chain} ${styles.chainDraft}`)}
        {draft?.points.map((p, i) => {
          const s = toScreen(p);
          return s ? <circle key={i} cx={s[0]} cy={s[1]} r={3.5} className={geo.chainPoint} /> : null;
        })}
        {/* The tape: from the hook (or its foot on the edge) to the pointer. */}
        {shownReading &&
          (() => {
            const d = poly([shownReading.from, shownReading.to]);
            const f = toScreen(shownReading.from);
            return (
              <g className={styles.tape}>
                {d && <polyline points={d} />}
                {f && shownReading.mode === 'edge' && <rect x={f[0] - 3} y={f[1] - 3} width={6} height={6} />}
              </g>
            );
          })()}
        {tape?.line && (() => {
          const d = poly(tape.line);
          return d ? <polyline points={d} className={`${geo.guide} ${geo.traceGuide}`} style={{ stroke: 'var(--dim-temp)' }} /> : null;
        })()}
        {axisGuide && inf && <polyline points={axisGuide} className={geo.guide} style={{ stroke: `var(${INFERENCE_TOKEN[inf.kind]})` }} />}
        <TrackLines tracks={inf?.tracks} points={tracked} toScreen={toScreen} />
        {infAt && inf && <Glyph kind={inf.kind} at={infAt} />}
      </svg>
      {chainGeo?.stretches.map((s, i) => label(s.at, fmt(s.value), geo.dimStatic, `draft-${i}`))}
      {shownReading && shownReading.value > 1e-3 && label([(shownReading.from[0] + shownReading.to[0]) / 2, (shownReading.from[1] + shownReading.to[1]) / 2], `${fmt(shownReading.value)} m`, geo.liveDim, 'tape')}
      {infAt && inf?.tip && <ScreenTip inf={inf} at={infAt} />}
      {pointerAt && boxField && (
        <span
          className={geo.measure}
          // Above the pointer near the bottom, clear of the options strip and the dock.
          style={{ transform: `translate(${(pointerAt[0] + 16).toFixed(1)}px, ${(pointerAt[1] > (layerRef.current?.clientHeight ?? 1e9) - BOX_CLEAR_PX ? pointerAt[1] - 76 : pointerAt[1] + 16).toFixed(1)}px)` }}
          role="status"
        >
          <span className={geo.measureBox}>
            <small>{boxField}</small>
            <b>
              {typed ? typed : <span className={geo.measureLive}>{boxLive}</span>}
              <u aria-hidden="true" />
              {' m'}
            </b>
          </span>
          <span className={geo.measureCap}>Skriv et tal · ↵ sæt</span>
        </span>
      )}
    </div>
  );
}
